(() => {
  const ATTR = 'data-hf-ba-id';

  const parseMs = (raw, fallback) => {
    const v = (raw || '').trim();
    if (!v) return fallback;
    if (v.endsWith('ms')) return parseFloat(v) || fallback;
    if (v.endsWith('s')) return parseFloat(v) * 1000 || fallback;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const boot = (root) => {
    if (!root) return;

    const wrap = root.querySelector('.hf-ba__wrapper');
    const cursor = root.querySelector('.hf-ba__cursor');
    const handle = root.querySelector('.hf-ba__handle');

    if (!wrap || !cursor || !handle) return;
    if (wrap.dataset.baInit === '1') return;
    wrap.dataset.baInit = '1';

    const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const mqDesktop = window.matchMedia && window.matchMedia('(min-width: 768px)');

    const getDuration = () => parseMs(getComputedStyle(wrap).getPropertyValue('--ba-hint-duration'), 1400);
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    const getLimits = () => {
      const r = wrap.getBoundingClientRect();
      const w = r.width || 0;
      if (w < 10) return { min: 0, max: 100 };
      const cursorW = cursor.getBoundingClientRect().width || 0;
      const pxOffset = cursorW ? cursorW / 2 : 0;
      return { min: (pxOffset / w) * 100, max: 100 - (pxOffset / w) * 100 };
    };

    const setOffscreen = () => {
      const base = mqDesktop && mqDesktop.matches ? 97.8 : 95;
      const { min, max } = getLimits();
      wrap.style.setProperty('--ba-percent', clamp(base, min, max) + '%', 'important');
      wrap.removeAttribute('data-ba-visible');
    };

    const setCenter = () => {
      wrap.style.setProperty('--ba-percent', '50%', 'important');
      wrap.setAttribute('data-ba-visible', '1');
    };

    let hasAnimated = false;
    let hasInteracted = false;
    let pendingStart = false;

    const startWhenReallyVisible = () => {
      if (pendingStart || hasAnimated || hasInteracted || reduced) return;
      pendingStart = true;

      const tick = () => {
        if (hasAnimated || hasInteracted || reduced) { pendingStart = false; return; }

        const r = wrap.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.bottom <= 0 || r.top >= vh) { pendingStart = false; return; }

        const cs = getComputedStyle(root);
        const op = parseFloat(cs.opacity);
        if (cs.display === 'none' || cs.visibility === 'hidden' || (Number.isFinite(op) && op < 0.05)) {
          requestAnimationFrame(tick);
          return;
        }

        animateIn();
        pendingStart = false;
      };

      requestAnimationFrame(tick);
    };

    const animateIn = () => {
      if (hasAnimated || hasInteracted || reduced) return;

      wrap.classList.remove('is-animating');
      setOffscreen();

      requestAnimationFrame(() => {
        wrap.classList.add('is-animating');
        void wrap.offsetWidth;

        requestAnimationFrame(() => {
          setCenter();
          hasAnimated = true;
          setTimeout(() => wrap.classList.remove('is-animating'), getDuration() + 80);
        });
      });
    };

    if (reduced) {
      setCenter();
    } else {
      setOffscreen();

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) { startWhenReallyVisible(); break; }
            }
          },
          { rootMargin: '0px 0px -15% 0px', threshold: [0, 0.25] }
        );
        io.observe(wrap);
      } else {
        const checkVisible = () => {
          if (hasAnimated || hasInteracted) return;
          const rect = wrap.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top <= vh * 0.85 && rect.bottom >= vh * 0.15) {
            startWhenReallyVisible();
            window.removeEventListener('scroll', checkVisible);
          }
        };
        window.addEventListener('scroll', checkVisible, { passive: true });
        checkVisible();
      }

      const sync = () => {
        if (wrap.hasAttribute('data-ba-visible') || hasAnimated || hasInteracted) return;
        setOffscreen();
      };
      mqDesktop?.addEventListener?.('change', sync);
    }

    const setPercentFromClientX = (clientX) => {
      const r = wrap.getBoundingClientRect();
      const w = r.width || 1;
      const { min, max } = getLimits();
      const percent = clamp((((clientX - r.left) / w) * 100), min, max);
      wrap.style.setProperty('--ba-percent', percent + '%', 'important');
      wrap.setAttribute('data-ba-visible', '1');
    };

    let dragging = false;
    let activePointerId = null;
    let startX = 0;
    let startY = 0;
    let isHorizontalDrag = false;

    const onMove = (e) => {
      if (!dragging) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!isHorizontalDrag) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { stopDrag(); return; }
        isHorizontalDrag = true;
        handle.setPointerCapture?.(activePointerId);
      }

      e.preventDefault();
      setPercentFromClientX(e.clientX);
    };

    const stopDrag = () => {
      if (!dragging) return;
      dragging = false;
      activePointerId = null;
      isHorizontalDrag = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
    };

    const startDrag = (e) => {
      if (e.button != null && e.button !== 0) return;

      hasInteracted = true;
      wrap.classList.remove('is-animating');
      wrap.setAttribute('data-ba-visible', '1');

      dragging = true;
      activePointerId = e.pointerId ?? null;
      startX = e.clientX;
      startY = e.clientY;
      isHorizontalDrag = false;

      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', stopDrag, { passive: true });
      window.addEventListener('pointercancel', stopDrag, { passive: true });
    };

    handle.addEventListener('pointerdown', startDrag, { passive: true });

    wrap.addEventListener('click', (e) => {
      if (e.target === handle || handle.contains(e.target)) return;
      hasInteracted = true;
      wrap.classList.remove('is-animating');
      setPercentFromClientX(e.clientX);
    }, { passive: true });
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[' + ATTR + ']').forEach(boot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
